import {
  ModernizedActivityEntityType,
  ModernizedActivityStatus,
  ModernizedCourseContentSectionType,
  type GetCourseSnapshotReviewQuery,
} from "@axio/graphql/generated/graphql";

import type { SnapshotVersionEntry } from "./components/VersionHistorySheet";
import type {
  ActivityDetail,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewSidebarGroup,
  CourseEvaluatorReviewSidebarItem,
  CourseEvaluatorReviewSidebarMeta,
} from "./types/reviewTypes";

export type HistoricalSnapshotDisplayState = {
  snapshot: SnapshotVersionEntry;
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
};

export type CourseSnapshotReviewPayload = NonNullable<GetCourseSnapshotReviewQuery["getCourseSnapshotReview"]["data"]>;
type CourseSnapshotActivityPayload = CourseSnapshotReviewPayload["activities"][number];
type CourseSnapshotModulePayload = CourseSnapshotReviewPayload["modules"][number];
type CourseSnapshotContentSectionPayload = CourseSnapshotReviewPayload["contentSections"][number];

const COURSE_LEVEL_GROUP_ID = "course-level-content";

const CONTENT_SECTION_TITLES: Record<string, string> = {
  [ModernizedCourseContentSectionType.LearningObjectives]: "Learning Objectives",
  [ModernizedCourseContentSectionType.CourseMaterialsList]: "Course Materials List",
  [ModernizedCourseContentSectionType.InstructorAvailability]: "Instructor Availability",
  [ModernizedCourseContentSectionType.Other]: "Other",
};

function getSnapshotStatus(value: string | null | undefined): ModernizedActivityStatus | null | undefined {
  if (value == null) {
    return value;
  }

  return Object.values(ModernizedActivityStatus).includes(value as ModernizedActivityStatus)
    ? (value as ModernizedActivityStatus)
    : undefined;
}

function isReviewedStatus(status: ModernizedActivityStatus | null | undefined): boolean {
  return (
    status === ModernizedActivityStatus.Queued ||
    status === ModernizedActivityStatus.Skipped ||
    status === ModernizedActivityStatus.Published
  );
}

function toDisplayDate(value: string | null | undefined): string | undefined {
  if (!value) {
    return undefined;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString("en-US");
}

function getActivityEntityType(snapshotType: string): ModernizedActivityEntityType {
  switch (snapshotType) {
    case "assignment":
      return ModernizedActivityEntityType.Assignment;
    case "assessment":
      return ModernizedActivityEntityType.Assessment;
    case "axioResource":
      return ModernizedActivityEntityType.AxioResource;
    case "project":
      return ModernizedActivityEntityType.Project;
    case "ileRecommendation":
      return ModernizedActivityEntityType.IleRecommendation;
    default:
      return ModernizedActivityEntityType.OtherActivity;
  }
}

function getActivityItemType(snapshotType: string): CourseEvaluatorReviewItemType {
  switch (snapshotType) {
    case "assignment":
      return "assignment";
    case "assessment":
      return "quiz";
    case "project":
      return "project";
    case "ileRecommendation":
      return "ile";
    case "axioResource":
      return "resource";
    default:
      return "resource";
  }
}

function getActivityItemKind(snapshotType: string): CourseEvaluatorReviewSidebarItem["kind"] {
  return snapshotType === "ileRecommendation" || snapshotType === "axioResource" ? "suggestedContent" : "assignment";
}

function buildSnapshotMeta(itemType: CourseEvaluatorReviewItemType): CourseEvaluatorReviewSidebarMeta[] {
  return [{ kind: "itemType", itemType }];
}

function buildItemTemplateMap(review: CourseEvaluatorReviewData): Map<string, CourseEvaluatorReviewSidebarItem> {
  return new Map(review.sidebarGroups.flatMap((group) => group.items).map((item) => [item.id, item]));
}

function buildGroupTemplateMap(review: CourseEvaluatorReviewData): Map<string, CourseEvaluatorReviewSidebarGroup> {
  return new Map(review.sidebarGroups.map((group) => [group.id, group]));
}

function orderModules(snapshotData: CourseSnapshotReviewPayload): CourseSnapshotModulePayload[] {
  const modulesById = new Map(snapshotData.modules.map((moduleSnapshot) => [moduleSnapshot.id, moduleSnapshot]));
  const ordered = snapshotData.course.moduleOrder
    .map((moduleId) => modulesById.get(moduleId))
    .filter((moduleSnapshot): moduleSnapshot is CourseSnapshotModulePayload => Boolean(moduleSnapshot));
  const orderedIds = new Set(ordered.map((moduleSnapshot) => moduleSnapshot.id));
  return [...ordered, ...snapshotData.modules.filter((moduleSnapshot) => !orderedIds.has(moduleSnapshot.id))];
}

function orderModuleActivities(
  moduleSnapshot: CourseSnapshotModulePayload,
  activities: CourseSnapshotActivityPayload[]
): CourseSnapshotActivityPayload[] {
  const activitiesById = new Map(activities.map((activitySnapshot) => [activitySnapshot.id, activitySnapshot]));
  const ordered = moduleSnapshot.activityOrder
    .map((activityId) => activitiesById.get(activityId))
    .filter((activitySnapshot): activitySnapshot is CourseSnapshotActivityPayload => Boolean(activitySnapshot));
  const orderedIds = new Set(ordered.map((activitySnapshot) => activitySnapshot.id));
  return [...ordered, ...activities.filter((activitySnapshot) => !orderedIds.has(activitySnapshot.id))];
}

function buildModuleDescriptionItem({
  moduleSnapshot,
  template,
}: {
  moduleSnapshot: CourseSnapshotModulePayload;
  template: CourseEvaluatorReviewSidebarItem | undefined;
}): CourseEvaluatorReviewSidebarItem {
  return {
    id: moduleSnapshot.id,
    title: moduleSnapshot.recommendedTitle ?? moduleSnapshot.title ?? "Untitled Module",
    kind: "moduleDescription",
    entityType: ModernizedActivityEntityType.Module,
    status: getSnapshotStatus(moduleSnapshot.status),
    meta: template?.meta ?? buildSnapshotMeta("moduleDescription"),
  };
}

function buildActivityItem({
  activitySnapshot,
  template,
}: {
  activitySnapshot: CourseSnapshotActivityPayload;
  template: CourseEvaluatorReviewSidebarItem | undefined;
}): CourseEvaluatorReviewSidebarItem {
  const fallbackItemType = getActivityItemType(activitySnapshot.type);
  return {
    id: activitySnapshot.id,
    title: activitySnapshot.recommendedTitle ?? activitySnapshot.title ?? "Untitled Activity",
    kind: template?.kind ?? getActivityItemKind(activitySnapshot.type),
    entityType: template?.entityType ?? getActivityEntityType(activitySnapshot.type),
    status: getSnapshotStatus(activitySnapshot.status),
    externalVisibility: template?.externalVisibility,
    meta: template?.meta ?? buildSnapshotMeta(fallbackItemType),
  };
}

function buildContentSectionItem({
  sectionSnapshot,
  template,
}: {
  sectionSnapshot: CourseSnapshotContentSectionPayload;
  template: CourseEvaluatorReviewSidebarItem | undefined;
}): CourseEvaluatorReviewSidebarItem {
  return {
    id: sectionSnapshot.id,
    title: CONTENT_SECTION_TITLES[sectionSnapshot.sectionType] ?? sectionSnapshot.sectionType,
    kind: "recommendation",
    entityType: ModernizedActivityEntityType.ContentSection,
    status: getSnapshotStatus(sectionSnapshot.status),
    meta: template?.meta,
  };
}

function buildModuleDetail(
  moduleSnapshot: CourseSnapshotModulePayload,
  template: ActivityDetail | undefined
): ActivityDetail {
  const currentDescription = moduleSnapshot.description ?? "";
  return {
    ...template,
    id: moduleSnapshot.id,
    currentTitle: moduleSnapshot.title ?? "",
    currentDescription,
    recommendedTitle: moduleSnapshot.recommendedTitle ?? moduleSnapshot.title ?? "",
    recommendedDescription: moduleSnapshot.recommendedDescription ?? currentDescription,
    documentReference: "Content",
  };
}

function buildActivityDetail(
  moduleSnapshot: CourseSnapshotModulePayload,
  activitySnapshot: CourseSnapshotActivityPayload,
  template: ActivityDetail | undefined
): ActivityDetail {
  const currentDescription = "";
  const recommendedDueDate = activitySnapshot.recommendedDueDateCleared
    ? undefined
    : toDisplayDate(activitySnapshot.recommendedDueDate);

  return {
    ...template,
    id: activitySnapshot.id,
    currentTitle: activitySnapshot.title ?? "",
    recommendedTitle: activitySnapshot.recommendedTitle ?? activitySnapshot.title ?? "",
    recommendedDescription: activitySnapshot.recommendedDescription ?? currentDescription,
    activityType: getActivityItemType(activitySnapshot.type),
    suggestedContentType: getActivityItemType(activitySnapshot.type),
    recommendedPointsPossible: activitySnapshot.recommendedPointsPossible ?? null,
    recommendedDueDate,
    recommendedDueDateCleared: activitySnapshot.recommendedDueDateCleared ?? undefined,
    createdIleId: activitySnapshot.aiTutorId ?? undefined,
    createdIleTitle: activitySnapshot.aiTutorTitle ?? undefined,
    createdIleDescription: activitySnapshot.aiTutorDescription ?? undefined,
    createdIleDueDate: activitySnapshot.aiTutorDueDate ?? undefined,
    documentReference: `Content > ${moduleSnapshot.title ?? "Module"} > ${activitySnapshot.title ?? "Activity"}`,
  };
}

function buildContentSectionDetail(
  sectionSnapshot: CourseSnapshotContentSectionPayload,
  template: ActivityDetail | undefined
): ActivityDetail {
  const currentDescription =
    sectionSnapshot.valueHtml ?? sectionSnapshot.valueText ?? template?.currentDescription ?? "";
  return {
    ...template,
    id: sectionSnapshot.id,
    currentDescription,
    recommendedDescription: sectionSnapshot.recommendedValue ?? currentDescription,
  };
}

function updateReviewCounts(
  review: CourseEvaluatorReviewData,
  sidebarGroups: CourseEvaluatorReviewSidebarGroup[]
): CourseEvaluatorReviewData {
  const allItems = sidebarGroups.flatMap((group) => group.items);
  const queuedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Queued).length;
  const skippedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Skipped).length;
  const publishedCount = allItems.filter((item) => item.status === ModernizedActivityStatus.Published).length;

  return {
    ...review,
    sidebarGroups,
    totalCount: allItems.length,
    queuedCount,
    skippedCount,
    publishedCount,
    reviewedCount: queuedCount + skippedCount + publishedCount,
  };
}

export function buildHistoricalSnapshotDisplayState({
  snapshot,
  review,
  activityDetails,
  snapshotData,
}: {
  snapshot: SnapshotVersionEntry;
  review: CourseEvaluatorReviewData;
  activityDetails: Record<string, ActivityDetail>;
  snapshotData: CourseSnapshotReviewPayload;
}): HistoricalSnapshotDisplayState {
  const itemTemplatesById = buildItemTemplateMap(review);
  const groupTemplatesById = buildGroupTemplateMap(review);
  const activityDetailsById: Record<string, ActivityDetail> = {};

  const courseLevelItems = snapshotData.contentSections.map((sectionSnapshot) => {
    activityDetailsById[sectionSnapshot.id] = buildContentSectionDetail(
      sectionSnapshot,
      activityDetails[sectionSnapshot.id]
    );
    return buildContentSectionItem({
      sectionSnapshot,
      template: itemTemplatesById.get(sectionSnapshot.id),
    });
  });
  const courseLevelTemplate = groupTemplatesById.get(COURSE_LEVEL_GROUP_ID);
  const courseLevelGroup: CourseEvaluatorReviewSidebarGroup = {
    ...(courseLevelTemplate ?? {
      id: COURSE_LEVEL_GROUP_ID,
      title: "Course-Level Content",
      defaultExpanded: true,
      items: [],
    }),
    items: courseLevelItems,
    decided: courseLevelItems.filter((item) => isReviewedStatus(item.status)).length,
    total: courseLevelItems.length,
  };

  const activitiesByModuleId = new Map<string, CourseSnapshotActivityPayload[]>();
  snapshotData.activities.forEach((activitySnapshot) => {
    activitiesByModuleId.set(activitySnapshot.moduleId, [
      ...(activitiesByModuleId.get(activitySnapshot.moduleId) ?? []),
      activitySnapshot,
    ]);
  });

  const moduleGroups = orderModules(snapshotData).map((moduleSnapshot, index) => {
    const moduleItem = buildModuleDescriptionItem({
      moduleSnapshot,
      template: itemTemplatesById.get(moduleSnapshot.id),
    });
    activityDetailsById[moduleSnapshot.id] = buildModuleDetail(moduleSnapshot, activityDetails[moduleSnapshot.id]);

    const activityItems = orderModuleActivities(moduleSnapshot, activitiesByModuleId.get(moduleSnapshot.id) ?? []).map(
      (activitySnapshot) => {
        activityDetailsById[activitySnapshot.id] = buildActivityDetail(
          moduleSnapshot,
          activitySnapshot,
          activityDetails[activitySnapshot.id]
        );
        return buildActivityItem({
          activitySnapshot,
          template: itemTemplatesById.get(activitySnapshot.id),
        });
      }
    );
    const items = [moduleItem, ...activityItems];
    const groupTemplate = groupTemplatesById.get(moduleSnapshot.id);

    return {
      ...(groupTemplate ?? {
        id: moduleSnapshot.id,
        title: moduleSnapshot.title ?? "Untitled Module",
        items: [],
      }),
      title: moduleSnapshot.recommendedTitle ?? moduleSnapshot.title ?? groupTemplate?.title ?? "Untitled Module",
      items,
      decided: items.filter((item) => isReviewedStatus(item.status)).length,
      total: items.length,
      defaultExpanded: groupTemplate?.defaultExpanded ?? index === 0,
    };
  });

  return {
    snapshot,
    review: updateReviewCounts(
      {
        ...review,
        courseName: snapshotData.course.title ?? review.courseName,
        score: snapshotData.course.totalScore ?? review.score,
        strengths: snapshotData.course.strengths,
        improvements: snapshotData.course.itemsToImprove,
      },
      [courseLevelGroup, ...moduleGroups]
    ),
    activityDetails: activityDetailsById,
  };
}
